/**
 * Super Admin Dashboard - 대시보드 페이지
 * 
 * 전체 워크스페이스/구독 현황 요약
 */

import { Trans } from '@lingui/macro';
import { Building2, CreditCard, FileText, TrendingUp, AlertTriangle, Loader2, Layers, HardDrive, ChevronRight, X, MessageCircle, Clock, Receipt, AlertCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { cn } from '../../lib/utils';
import { Fire } from '../../services/FireClient';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';

interface StatCardProps {
    title: string;
    value: string | number;
    description?: string;
    icon: React.ElementType;
    trend?: 'up' | 'down' | 'neutral';
    trendValue?: string;
    variant?: 'default' | 'warning' | 'success';
    loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
    title, value, description, icon: Icon, trend, trendValue, variant = 'default', loading = false
}) => {
    const variantStyles = {
        default: 'bg-white border-slate-200/60',
        warning: 'bg-amber-50/50 border-amber-200/60',
        success: 'bg-emerald-50/50 border-emerald-200/60',
    };

    return (
        <Card className={cn('transition-all hover:shadow-md', variantStyles[variant])}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">{title}</CardTitle>
                <div className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center',
                    variant === 'warning' ? 'bg-amber-100 text-amber-600' :
                        variant === 'success' ? 'bg-emerald-100 text-emerald-600' :
                            'bg-slate-100 text-slate-600'
                )}>
                    <Icon className="w-4 h-4" />
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                ) : (
                    <>
                        <div className="text-2xl font-bold text-slate-900">{value}</div>
                        {description && (
                            <p className="text-xs text-slate-500 mt-1">{description}</p>
                        )}
                        {trend && trendValue && (
                            <div className={cn(
                                'flex items-center gap-1 mt-2 text-xs font-medium',
                                trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-slate-500'
                            )}>
                                <TrendingUp className={cn('w-3 h-3', trend === 'down' && 'rotate-180')} />
                                <span>{trendValue}</span>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

interface DashboardStats {
    totalWorkspaces: number;
    activeSubscriptions: number;
    expiringThisMonth: number;
    pendingInvoices: number;
    totalRevenue: number;
    tierBreakdown: { [tier: string]: number };
}

interface StorageAlert {
    workspaceId: string;
    workspaceName: string;
    usagePercent: number;
    usedBytes: number;
    limitBytes: number;
}

interface ActionItem {
    id: string;
    type: 'inquiry' | 'expiring' | 'invoice_pending' | 'overdue';
    title: string;
    description: string;
    link: string;
    priority: 'high' | 'medium' | 'low';
    count?: number;
}

const SuperAdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<DashboardStats>({
        totalWorkspaces: 0,
        activeSubscriptions: 0,
        expiringThisMonth: 0,
        pendingInvoices: 0,
        totalRevenue: 0,
        tierBreakdown: {},
    });
    const [storageAlerts, setStorageAlerts] = useState<StorageAlert[]>([]);
    const [showStorageModal, setShowStorageModal] = useState(false);
    const [actionItems, setActionItems] = useState<ActionItem[]>([]);

    useEffect(() => {
        loadDashboardStats();
    }, []);

    const loadDashboardStats = async () => {
        setLoading(true);
        try {
            console.log('[Dashboard] Loading stats...');

            // 1. 캐시된 대시보드 통계 읽기 (1 read)
            const statsDoc = await getDoc(doc(Fire.store, 'admin', 'dashboardStats'));
            const cachedStats = statsDoc.data();

            // 2. 워크스페이스 수는 count 쿼리 사용 (1 read, 문서 수에 관계없이)
            const { getCountFromServer } = await import('firebase/firestore');
            const countSnap = await getCountFromServer(collection(Fire.store, 'workspaces'));
            const totalWorkspaces = countSnap.data().count;

            // 3. 유료 워크스페이스 수 (tier != FREE) - count 쿼리
            const paidCountSnap = await getCountFromServer(
                query(collection(Fire.store, 'workspaces'), where('tier', '!=', 'FREE'))
            );
            const activeSubscriptions = paidCountSnap.data().count;

            // 4. 캐시된 통계 사용 (있으면)
            const expiringThisMonth = cachedStats?.subscriptions?.expiringIn30Days || 0;
            const pendingInvoices = cachedStats?.invoices?.issued || 0;
            const totalRevenue = cachedStats?.revenue?.monthly || 0;
            const tierBreakdown = cachedStats?.tierBreakdown || {};

            console.log('[Dashboard] Stats loaded with count queries');
            console.log('[Dashboard] Total workspaces:', totalWorkspaces, 'Active:', activeSubscriptions);

            setStats({
                totalWorkspaces,
                activeSubscriptions,
                expiringThisMonth,
                pendingInvoices,
                totalRevenue,
                tierBreakdown,
            });

            // 저장소 경고 목록 (캐시에서)
            const alerts: StorageAlert[] = cachedStats?.storageAlerts || [];
            setStorageAlerts(alerts);

            // Action Required 항목 구성
            const actions: ActionItem[] = [];

            // 새 문의 확인
            const newInquiriesCount = cachedStats?.inquiries?.newCount || 0;
            if (newInquiriesCount > 0) {
                actions.push({
                    id: 'new-inquiries',
                    type: 'inquiry',
                    title: '未回答の問い合わせ',
                    description: `${newInquiriesCount}件の問い合わせが回答待ちです`,
                    link: '/super-admin/inquiries',
                    priority: 'high',
                    count: newInquiriesCount,
                });
            }

            // 만료 예정 구독
            if (expiringThisMonth > 0) {
                actions.push({
                    id: 'expiring-subscriptions',
                    type: 'expiring',
                    title: '今月末で期限切れ',
                    description: `${expiringThisMonth}件のサブスクリプションが今月末で期限切れ`,
                    link: '/super-admin/revenue/subscriptions',
                    priority: 'medium',
                    count: expiringThisMonth,
                });
            }

            // 미발행 청구서 (issued 상태 = 발행됨, 입금 대기 중)
            if (pendingInvoices > 0) {
                actions.push({
                    id: 'pending-invoices',
                    type: 'invoice_pending',
                    title: '入金待ち請求書',
                    description: `${pendingInvoices}件の請求書が入金待ちです`,
                    link: '/super-admin/revenue/invoices',
                    priority: 'medium',
                    count: pendingInvoices,
                });
            }

            // 연체 청구서
            const overdueCount = cachedStats?.invoices?.overdue || 0;
            if (overdueCount > 0) {
                actions.push({
                    id: 'overdue-invoices',
                    type: 'overdue',
                    title: '延滞中の請求書',
                    description: `${overdueCount}件の請求書が期日を過ぎています`,
                    link: '/super-admin/revenue/invoices',
                    priority: 'high',
                    count: overdueCount,
                });
            }

            setActionItems(actions);

        } catch (error) {
            console.error('Failed to load dashboard stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 px-6 py-4">
                <h1 className="text-xl font-bold text-slate-900"><Trans>Dashboard</Trans></h1>
                <p className="text-sm text-slate-500 mt-0.5">
                    <Trans>Overview of workspace subscriptions and billing</Trans>
                </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50/30">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <StatCard
                        title="Total Workspaces"
                        value={stats.totalWorkspaces}
                        icon={Building2}
                        description="All registered workspaces"
                        loading={loading}
                    />
                    <StatCard
                        title="Active Subscriptions"
                        value={stats.activeSubscriptions}
                        icon={CreditCard}
                        variant="success"
                        description="Paid plans only"
                        loading={loading}
                    />
                    <StatCard
                        title="Expiring Soon"
                        value={stats.expiringThisMonth}
                        icon={AlertTriangle}
                        variant={stats.expiringThisMonth > 0 ? 'warning' : 'default'}
                        description="Within 30 days"
                        loading={loading}
                    />
                    <StatCard
                        title="Pending Invoices"
                        value={stats.pendingInvoices}
                        icon={FileText}
                        description="Awaiting payment"
                        loading={loading}
                    />
                </div>

                {/* Action Required Section */}
                <Card className={cn(
                    "mb-6",
                    actionItems.length > 0 ? "border-red-200 bg-red-50/30" : ""
                )}>
                    <CardHeader className="pb-2">
                        <CardTitle className={cn(
                            "text-base flex items-center gap-2",
                            actionItems.length > 0 ? "text-red-700" : "text-slate-700"
                        )}>
                            <AlertCircle className="w-4 h-4" />
                            <Trans>Action Required</Trans>
                            {actionItems.length > 0 && (
                                <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                                    {actionItems.length}
                                </span>
                            )}
                        </CardTitle>
                        <CardDescription className={actionItems.length > 0 ? "text-red-600" : "text-slate-500"}>
                            <Trans>Items that need your immediate attention</Trans>
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center py-6">
                                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                            </div>
                        ) : actionItems.length === 0 ? (
                            <div className="flex items-center justify-center py-6 text-slate-400">
                                <div className="text-center">
                                    <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm"><Trans>No pending actions</Trans></p>
                                    <p className="text-xs text-slate-400 mt-1"><Trans>All items are up to date</Trans></p>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {actionItems.map((item) => (
                                    <div
                                        key={item.id}
                                        onClick={() => navigate(item.link)}
                                        className={cn(
                                            "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border",
                                            item.priority === 'high'
                                                ? "bg-red-50 border-red-200 hover:bg-red-100"
                                                : "bg-amber-50 border-amber-200 hover:bg-amber-100"
                                        )}
                                    >
                                        <div className={cn(
                                            "w-10 h-10 rounded-full flex items-center justify-center",
                                            item.priority === 'high' ? "bg-red-100" : "bg-amber-100"
                                        )}>
                                            {item.type === 'inquiry' && <MessageCircle className={cn("w-5 h-5", item.priority === 'high' ? "text-red-600" : "text-amber-600")} />}
                                            {item.type === 'expiring' && <Clock className={cn("w-5 h-5", item.priority === 'high' ? "text-red-600" : "text-amber-600")} />}
                                            {item.type === 'invoice_pending' && <Receipt className={cn("w-5 h-5", item.priority === 'high' ? "text-red-600" : "text-amber-600")} />}
                                            {item.type === 'overdue' && <AlertTriangle className={cn("w-5 h-5", item.priority === 'high' ? "text-red-600" : "text-amber-600")} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={cn(
                                                    "text-sm font-semibold",
                                                    item.priority === 'high' ? "text-red-700" : "text-amber-700"
                                                )}>
                                                    {item.title}
                                                </span>
                                                {item.count && (
                                                    <span className={cn(
                                                        "text-xs px-1.5 py-0.5 rounded-full font-medium",
                                                        item.priority === 'high'
                                                            ? "bg-red-200 text-red-700"
                                                            : "bg-amber-200 text-amber-700"
                                                    )}>
                                                        {item.count}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-600 truncate">{item.description}</p>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Revenue Card */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg"><Trans>Monthly Revenue</Trans></CardTitle>
                        <CardDescription><Trans>Estimated monthly revenue from active subscriptions</Trans></CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                        ) : (
                            <>
                                <div className="text-3xl font-bold text-slate-900">{formatCurrency(stats.totalRevenue)}</div>
                                <p className="text-sm text-slate-500 mt-2">
                                    <Trans>Based on {stats.activeSubscriptions} active paid subscriptions</Trans>
                                </p>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Storage Alerts Card */}
                <Card className={cn("mb-6", storageAlerts.length > 0 ? "border-amber-200 bg-amber-50/50" : "")}>
                    <CardHeader className="pb-2">
                        <CardTitle className={cn("text-base flex items-center gap-2", storageAlerts.length > 0 ? "text-amber-700" : "text-slate-700")}>
                            <HardDrive className="w-4 h-4" />
                            <Trans>Storage Alerts</Trans>
                            {storageAlerts.length > 0 && (
                                <span className="ml-auto bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                                    {storageAlerts.length}
                                </span>
                            )}
                        </CardTitle>
                        <CardDescription className={storageAlerts.length > 0 ? "text-amber-600" : "text-slate-500"}>
                            <Trans>Workspaces using 90% or more of storage quota</Trans>
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {storageAlerts.length === 0 ? (
                            <div className="flex items-center justify-center py-6 text-slate-400">
                                <div className="text-center">
                                    <HardDrive className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm"><Trans>No storage alerts</Trans></p>
                                    <p className="text-xs text-slate-400 mt-1"><Trans>All workspaces are within quota limits</Trans></p>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {storageAlerts.slice(0, 3).map((alert) => (
                                    <div
                                        key={alert.workspaceId}
                                        className="flex items-center justify-between p-2 bg-white rounded-lg border border-amber-200"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                "w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold",
                                                alert.usagePercent >= 100
                                                    ? "bg-red-100 text-red-600"
                                                    : "bg-amber-100 text-amber-600"
                                            )}>
                                                {alert.usagePercent}%
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-slate-700">{alert.workspaceName}</div>
                                                <div className="text-xs text-slate-500">
                                                    {(alert.usedBytes / 1024 / 1024 / 1024).toFixed(1)} GB / {(alert.limitBytes / 1024 / 1024 / 1024).toFixed(0)} GB
                                                </div>
                                            </div>
                                        </div>
                                        <a
                                            href={`/super-admin/workspaces/${alert.workspaceId}`}
                                            className="text-amber-600 hover:text-amber-700"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </a>
                                    </div>
                                ))}
                                {storageAlerts.length > 3 && (
                                    <button
                                        onClick={() => setShowStorageModal(true)}
                                        className="w-full text-center text-sm text-amber-600 hover:text-amber-700 py-2"
                                    >
                                        <Trans>View all {storageAlerts.length} workspaces</Trans>
                                    </button>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Storage Modal */}
                {showStorageModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden">
                            <div className="flex items-center justify-between p-4 border-b">
                                <h3 className="text-lg font-semibold flex items-center gap-2">
                                    <HardDrive className="w-5 h-5 text-amber-500" />
                                    <Trans>Storage Alerts</Trans>
                                    <span className="text-sm text-slate-500">({storageAlerts.length})</span>
                                </h3>
                                <button onClick={() => setShowStorageModal(false)} className="text-slate-400 hover:text-slate-600">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-4 overflow-y-auto max-h-[60vh] space-y-2">
                                {storageAlerts.map((alert) => (
                                    <a
                                        key={alert.workspaceId}
                                        href={`/super-admin/workspaces/${alert.workspaceId}`}
                                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                "w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold",
                                                alert.usagePercent >= 100
                                                    ? "bg-red-100 text-red-600"
                                                    : "bg-amber-100 text-amber-600"
                                            )}>
                                                {alert.usagePercent}%
                                            </div>
                                            <div>
                                                <div className="font-medium text-slate-700">{alert.workspaceName}</div>
                                                <div className="text-sm text-slate-500">
                                                    {(alert.usedBytes / 1024 / 1024 / 1024).toFixed(2)} GB / {(alert.limitBytes / 1024 / 1024 / 1024).toFixed(0)} GB
                                                </div>
                                            </div>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-slate-400" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Tier Breakdown & Quick Actions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Tier Breakdown */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                <Layers className="w-4 h-4" />
                                <Trans>Subscription by Tier</Trans>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex justify-center py-8">
                                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                                </div>
                            ) : Object.keys(stats.tierBreakdown).length > 0 ? (
                                <div className="space-y-3">
                                    {Object.entries(stats.tierBreakdown)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([tier, count]) => (
                                            <div key={tier} className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className={cn(
                                                        'w-3 h-3 rounded-full',
                                                        tier === 'FREE' ? 'bg-slate-400' :
                                                            tier === 'BASIC' ? 'bg-blue-500' :
                                                                tier === 'STANDARD' ? 'bg-indigo-500' :
                                                                    tier === 'PREMIUM' ? 'bg-purple-500' :
                                                                        tier === 'PREMIUM_PARTNER' ? 'bg-amber-500' :
                                                                            'bg-slate-300'
                                                    )} />
                                                    <span className="text-sm font-medium text-slate-700">{tier}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-semibold text-slate-900">{count}</span>
                                                    <span className="text-xs text-slate-500">
                                                        ({Math.round((count / stats.totalWorkspaces) * 100)}%)
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            ) : (
                                <div className="text-sm text-slate-500 text-center py-8">
                                    <Trans>No subscription data</Trans>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Quick Actions */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base"><Trans>Quick Actions</Trans></CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors border border-transparent hover:border-slate-100">
                                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                                    <CreditCard className="w-4 h-4 text-indigo-600" />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-slate-700"><Trans>Grant Subscription</Trans></div>
                                    <div className="text-xs text-slate-500"><Trans>Manually assign a plan to workspace</Trans></div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors border border-transparent hover:border-slate-100">
                                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                                    <FileText className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-slate-700"><Trans>Create Invoice</Trans></div>
                                    <div className="text-xs text-slate-500"><Trans>Generate a new billing document</Trans></div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminDashboard;

